import { Drawer } from 'vaul';
import { useEffect, type ReactNode } from 'react';
import { cn } from '../lib/cn.ts';

export interface GlassSheetProps {
  open: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Non-modal sheets sit over a map and let the map receive touches. */
  modal?: boolean;
  /** Snap points as fractions of viewport height, e.g. [0.18, 0.5, 0.92]. */
  snapPoints?: (number | string)[];
  activeSnapPoint?: number | string | null;
  onSnapChange?: (snap: number | string | null) => void;
  dismissible?: boolean;
  title?: ReactNode;
  description?: ReactNode;
  header?: ReactNode;
  children: ReactNode;
  className?: string;
  /** Extra classes for the scrolling body (e.g. bottom padding when a tab bar overlays the sheet). */
  bodyClassName?: string;
  /** Hide the drag handle. */
  noHandle?: boolean;
  /** No panel chrome — children float directly over the page (cards carry their own glass). */
  plain?: boolean;
  /** Portal container (defaults to body). */
  container?: HTMLElement | null;
}

/**
 * Bottom sheet built on vaul, skinned as glass-2 with 28px top radius and a drag handle.
 * Use `modal={false}` + snapPoints for map screens; default modal for pickers/confirmations.
 */
export function GlassSheet({
  open,
  onOpenChange,
  modal = true,
  snapPoints,
  activeSnapPoint,
  onSnapChange,
  dismissible = true,
  title,
  description,
  header,
  children,
  className,
  bodyClassName,
  noHandle,
  plain,
  container,
}: GlassSheetProps) {
  // vaul renders Radix Dialog in modal mode regardless of `modal`, which aria-hides everything else.
  // For non-modal sheets over a map that must stay reachable (a11y + tests), undo that.
  useEffect(() => {
    if (!open || modal || typeof document === 'undefined') return;
    const strip = () =>
      document.querySelectorAll('[data-aria-hidden]').forEach((el) => {
        el.removeAttribute('aria-hidden');
        el.removeAttribute('data-aria-hidden');
      });
    const mo = new MutationObserver(strip);
    mo.observe(document.body, { attributes: true, subtree: true, attributeFilter: ['aria-hidden'] });
    strip();
    return () => mo.disconnect();
  }, [open, modal]);
  return (
    <Drawer.Root
      open={open}
      onOpenChange={onOpenChange}
      modal={modal}
      dismissible={dismissible}
      snapPoints={snapPoints}
      activeSnapPoint={activeSnapPoint}
      setActiveSnapPoint={onSnapChange}
      repositionInputs={false}
    >
      <Drawer.Portal container={container ?? undefined}>
        {modal && <Drawer.Overlay className="fixed inset-0 z-[var(--m-z-modal)] bg-[var(--m-color-overlay)]" />}
        <Drawer.Content
          aria-describedby={description ? undefined : ''}
          className={cn(
            plain ? 'will-change-transform fixed inset-x-0 bottom-0 mx-auto flex max-h-[96dvh] w-full max-w-[var(--m-size-layout-max)] flex-col outline-none' : 'lg fixed inset-x-0 bottom-0 mx-auto flex max-h-[96dvh] w-full max-w-[var(--m-size-layout-max)] flex-col rounded-t-[var(--m-radius-3xl)] outline-none',
            // persistent map sheets sit under the tab bar (shown on wide screens); modal pickers go above everything
            modal ? 'z-[var(--m-z-modal)]' : 'z-[var(--m-z-sheet)]',
            snapPoints ? 'h-full max-h-[100dvh]' : '',
            className,
          )}
        >
          {!noHandle && (
            <div className="flex justify-center pt-2 pb-1">
              <div className="h-1.5 w-10 rounded-full bg-border-strong" />
            </div>
          )}
          {(title || description || header) && (
            <div className="px-5 pt-1 pb-2">
              {title && <Drawer.Title className="text-h3 font-semibold">{title}</Drawer.Title>}
              {description && <Drawer.Description className="text-caption text-text-secondary">{description}</Drawer.Description>}
              {header}
            </div>
          )}
          {!title && <Drawer.Title className="sr-only">sheet</Drawer.Title>}
          <div className={cn('m-scroll pb-safe flex-1 overflow-y-auto px-5 pb-5', bodyClassName)}>{children}</div>
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  );
}
